import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import UnifiedDashboardHeader from '../../components/common/UnifiedDashboardHeader';
import DashboardFooter from '../../components/dashboard/DashboardFooter';
import MemberDirectory from '../../components/members/MemberDirectory';
import ProfessionalLoader from "../../components/common/ProfessionalLoader";
import './Dashboard.css';

const UserDashboard = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const tokenRefreshInterval = useRef(null);

  const handleRefreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        return; // Silently fail if no refresh token
      }

      const response = await authService.refreshToken(refreshToken);

      if (response.success) {
        localStorage.setItem('access_token', response.data.access);
        // Silently refresh token without showing success message
      }
    } catch (error) {
      // Silently handle token refresh errors
    }
  };

  // Set up automatic token refresh every 20 minutes
  useEffect(() => {
    if (isAuthenticated) {
      // Refresh token every 20 minutes (1200000ms)
      tokenRefreshInterval.current = setInterval(() => {
        handleRefreshToken();
      }, 1200000);

      // Cleanup interval on unmount
      return () => {
        if (tokenRefreshInterval.current) {
          clearInterval(tokenRefreshInterval.current);
        }
      };
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        await authService.logout(refreshToken);
      }
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      navigate('/login');
    }
  };

  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <UnifiedDashboardHeader
        isAdmin={false}
        onLogout={handleLogout}
        userRole="user"
      />

      <main className="dashboard-main">
        {/* Member Directory Section */}
        <div className="member-directory-section">
          <MemberDirectory />
        </div>
      </main>

      <DashboardFooter />
    </div>
  );
};

export default UserDashboard;
